import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { motion } from "motion/react"
import QRCode from "qrcode"

import { formatOrderNumber } from "./ticketUtils"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type Props = {
    orderNumber: string
    filmName: string
    onClose: () => void
}

export default function TicketQrModal({ orderNumber, filmName, onClose }: Props) {
    const [qrSrc, setQrSrc] = useState<string | null>(null)
    const [qrError, setQrError] = useState(false)

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [onClose])

    useEffect(() => {
        let cancelled = false

        setQrSrc(null)
        setQrError(false)
        QRCode.toDataURL(orderNumber, {
            width: 440,
            margin: 2,
            errorCorrectionLevel: "M",
            color: { dark: "#ffffff", light: "#1a2d55" },
        })
            .then(dataUrl => {
                if (!cancelled) setQrSrc(dataUrl)
            })
            .catch(() => {
                if (!cancelled) setQrError(true)
            })

        return () => {
            cancelled = true
        }
    }, [orderNumber])

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8"
            style={{ background: "rgba(10,18,42,0.85)", backdropFilter: "blur(8px)" }}
            onClick={onClose}>
            <motion.div
                initial={{ scale: 0.94, y: 12 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 8, opacity: 0 }}
                transition={{ type: "spring", stiffness: 320, damping: 28 }}
                className="w-full max-w-sm overflow-hidden rounded-[24px] border border-white/12"
                style={{ background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)" }}
                onClick={e => e.stopPropagation()}>
                <div className="flex flex-col items-center gap-5 px-6 pt-6 pb-[max(1.5rem,var(--safe-bottom))]">
                    <div className="text-center">
                        <h3
                            className="text-white mb-1"
                            style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17 }}>
                            QR-код билета
                        </h3>
                        <p className="text-white/50 text-[13px]" style={{ fontFamily: BODY }}>
                            {filmName}
                        </p>
                    </div>
                    <div className="rounded-[20px] border border-white/10 bg-white/[0.04] p-4">
                        {qrSrc ? (
                            <img src={qrSrc} alt="QR-код билета" width={220} height={220} className="size-[220px]" />
                        ) : qrError ? (
                            <div className="flex size-[220px] items-center justify-center px-5 text-center text-[13px] leading-relaxed text-white/55" style={{ fontFamily: BODY }}>
                                Не удалось подготовить QR-код. Покажите номер заказа ниже.
                            </div>
                        ) : (
                            <div className="size-[220px] animate-pulse rounded-[10px] bg-white/[0.08]" aria-label="Готовим QR-код" />
                        )}
                    </div>
                    <div className="text-center">
                        <p className="text-white/40 text-[11px] uppercase tracking-[0.08em] mb-1" style={{ fontFamily: MONO }}>
                            Номер заказа
                        </p>
                        <p className="text-white text-[18px]" style={{ fontFamily: MONO, fontWeight: 800, letterSpacing: "0.06em" }}>
                            {formatOrderNumber(orderNumber)}
                        </p>
                    </div>
                    <p className="text-center text-white/45 text-[12px] leading-relaxed" style={{ fontFamily: BODY }}>
                        Покажите QR-код на входе в зал
                    </p>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full rounded-[14px] py-3.5 text-white text-[14px] transition-opacity hover:opacity-90"
                        style={{
                            background: "#349be6",
                            fontFamily: MONO,
                            fontWeight: 700,
                            boxShadow: "0 0 18px rgba(52,155,230,0.35)",
                        }}>
                        Закрыть
                    </button>
                </div>
            </motion.div>
        </motion.div>,
        document.body
    )
}
